import React, { useEffect, useMemo, useState } from 'react';
import { Card, Table, Form, Select, Button, Tag, Tooltip, Popover, Checkbox, Modal, InputNumber, Popconfirm, message, Radio, Upload, Space, Alert } from 'antd';
import { downloadExcelCombinedV2, downloadExcelCombinedV2Pdf, downloadExcelCombinedV2Summary, downloadExcelCombinedV2SummaryPdf, getExcelCombinedV2, getClients, getSubcontractors, getClientDepartments, getDepartments, updateExcelCombinedRowV2, getExcelCombinedRowVersionsV2, clearSummaryV2, createExcelUploadV2, processExcelUploadV2, getRatesheetsV2, deleteCombinedRowV2 } from '../api';
import moment from 'moment';
import { FaFileExcel, FaFilePdf, FaColumns, FaEdit, FaTrash } from 'react-icons/fa';
import { UploadOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';

const months = Array.from({ length: 12 }, (_, i) => ({ label: moment().month(i).format('MMMM'), value: i + 1 }));
const years = Array.from({ length: 8 }, (_, i) => ({ label: String(moment().year() - 3 + i), value: moment().year() - 3 + i }));

const ExcelCombinedView = () => {
  const now = moment();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialMonth = Number(searchParams.get('month')) || (now.month() + 1);
  const initialYear = Number(searchParams.get('year')) || now.year();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ month: initialMonth, year: initialYear, company: null, client: null, department: null, subcontractor: null });
  const [options, setOptions] = useState({ companies: [], clients: [], departments: [], subcontractors: [] });
  const [subcontractorOptions, setSubcontractorOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [clientDepartments, setClientDepartments] = useState({});
  const daysInMonth = useMemo(() => moment({ year: filters.year, month: filters.month - 1 }).daysInMonth(), [filters]);
  const [visibleCols, setVisibleCols] = useState({ type: false, company_subcontractor: false, client: false, department: false, skills: true, basic_salary: true, rate: true, allowance: true, loan: true, loan_deduction: true, other_deduction: true, cleaning: true, deductions: true });
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);

  // Upload States
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [ratesheetSource, setRatesheetSource] = useState('new');
  const [ratesheetOptions, setRatesheetOptions] = useState([]);
  const [ratesheetFile, setRatesheetFile] = useState(null);
  const [timesheetFile, setTimesheetFile] = useState(null);
  const [uploadForm] = Form.useForm();
  const [showMissingModal, setShowMissingModal] = useState(false);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch clients first
        const clientsRes = await getClients();
        if (clientsRes.success) {
          const clientOpts = clientsRes.data.map(client => ({
            label: client.name,
            value: client.name  // Use client name as value instead of ID
          }));
          setClientOptions(clientOpts);

          // Then fetch departments for each client
          const deptMap = {};
          await Promise.all(
            clientsRes.data.map(async client => {
              const deptRes = await getClientDepartments(client.id);
              if (deptRes.success) {
                deptMap[client.name] = deptRes.data.map(dept => ({
                  label: dept.name,
                  value: dept.name
                }));
              }
            })
          );
          setClientDepartments(deptMap);
        }

        // Fetch subcontractors
        const subRes = await getSubcontractors();
        if (subRes.success) {
          const subOptions = subRes.data.map(item => ({
            label: item.name,
            value: item.name
          }));
          setSubcontractorOptions(subOptions);
        }

        // Fetch departments for company dropdown
        const deptRes = await getDepartments();
        if (deptRes.success) {
          const deptOptions = deptRes.data.map(item => ({
            label: item.name,
            value: item.shortname,
            shortname: item.shortname || item.name.split(' ').map(w => w[0]).join('').toUpperCase()
          }));
          setDepartmentOptions(deptOptions);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // Update department options when client changes
  const handleClientChange = (clientName) => {
    setFilters(prev => ({
      ...prev,
      client: clientName,
      department: null // Reset department when client changes
    }));
  };

  const missingMatches = useMemo(() => rows.filter(r => !r.match_ok && r.name && r.name.trim() !== '' && r.name !== '-'), [rows]);

  const fetchData = async (filterValues = filters) => {
    setLoading(true);
    try {
      const res = await getExcelCombinedV2(filterValues);
      const data = res.data || {};
      setRows(data.rows || []);
      setOptions({
        companies: (data.filters?.companies || []).map(v => ({ label: v, value: v })),
        clients: (data.filters?.clients || []).map(v => ({ label: v, value: v })),
        departments: (data.filters?.departments || []).map(v => ({ label: v, value: v })),
        subcontractors: (data.filters?.subcontractors || []).map(v => ({ label: v, value: v })),
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcontractors on component mount
  useEffect(() => {
    const fetchSubcontractors = async () => {
      try {
        const res = await getSubcontractors();
        if (res.success) {
          const subcontractorOptions = res.data.map(sub => ({
            label: sub.name,
            value: sub.name
          }));
          setSubcontractorOptions(subcontractorOptions);
        }
      } catch (error) {
        console.error('Error fetching subcontractors:', error);
      }
    };

    fetchSubcontractors();
  }, []);

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };

    // If client is being cleared, also clear the department
    if (key === 'client' && !value) {
      newFilters.department = null;
    }

    setFilters(newFilters);
    fetchData(newFilters);
  };

  const showMissingCodesModal = (res) => {
    const codes = res.data?.missing_in_ratesheet || res.missing_in_ratesheet || [];
    setTimeout(() => {
      Modal.error({
        title: res.message || 'Processing Error',
        content: (
          <div>
            <p>The following employee codes from the timesheet were not found in the ratesheet:</p>
            <ul style={{ maxHeight: '250px', overflowY: 'auto', paddingLeft: '20px' }}>
              {codes.map(code => (
                <li key={code} style={{ marginBottom: '4px' }}><strong>{code}</strong></li>
              ))}
            </ul>
            <p>Please update the ratesheet with these codes and try again.</p>
          </div>
        ),
        width: 500,
      });
    }, 300);
  };

  const fetchRatesheets = async (month, year) => {
    const res = await getRatesheetsV2(month, year);
    if (res.success) {
      setRatesheetOptions((res.data || []).map(r => ({
        label: `${r.ratesheet_original_filename || ('Ratesheet #' + r.id)} (${String(r.month).padStart(2, '0')}-${r.year})`,
        value: r.id
      })));
    }
  };

  const onUploadSubmit = async () => {
    try {
      const vals = await uploadForm.validateFields();
      if (ratesheetSource === 'new' && !ratesheetFile && !timesheetFile) {
        message.error('Choose files');
        return;
      }
      setUploadLoading(true);
      setUploadError(null);
      const fd = new FormData();
      fd.append('month', vals.month);
      fd.append('year', vals.year);
      fd.append('ratesheet_source', ratesheetSource);
      if (ratesheetSource === 'previous' && vals.previous_ratesheet_id) fd.append('previous_ratesheet_id', vals.previous_ratesheet_id);
      if (ratesheetSource === 'new' && ratesheetFile) fd.append('ratesheet', ratesheetFile);
      if (timesheetFile) fd.append('timesheet', timesheetFile);

      const res = await createExcelUploadV2(fd);
      if (res.success) {
        message.loading({ content: 'Processing file...', key: 'processing' });
        const procRes = await processExcelUploadV2(res.data.id);
        if (procRes.success) {
          message.success({ content: 'Uploaded and Processed successfully', key: 'processing' });
          setUploadOpen(false);
          fetchData(filters);
        } else {
          message.destroy('processing');
          console.log('Processing failed:', procRes);
          setUploadError(procRes);
        }
      } else {
        console.log('Upload failed:', res);
        setUploadError(res);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setUploadLoading(false);
    }
  };

  // Initial data load
  useEffect(() => {
    fetchData();
  }, []);

  // Filter out rows with empty name or company info
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      const hasName = row.name && row.name.trim() !== '';
      const hasCompany = row.company_subcontractor && row.company_subcontractor.trim() !== '';
      return hasName && hasCompany;
    });
  }, [rows]);

  const dayColumns = useMemo(() => {
    return Array.from({ length: daysInMonth }, (_, i) => {
      const dayNum = i + 1;
      const isSunday = moment({ year: filters.year, month: filters.month - 1, date: dayNum }).day() === 0;
      return {
        title: String(dayNum),
        dataIndex: `day_${dayNum}`,
        width: 60,
        className: isSunday ? 'sunday-col' : undefined,
        onHeaderCell: () => (isSunday ? { style: { backgroundColor: '#fff1f0' } } : {}),
        onCell: (r) => (isSunday && r.match_ok !== false ? { style: { backgroundColor: '#fff5f5' } } : {}),
        render: (_, r) => r[`day_${dayNum}`] ?? null,
      };
    });
  }, [daysInMonth, filters.month, filters.year]);

  const getVal = (r, keys) => {
    for (const k of keys) {
      const v = r[k];
      if (v !== undefined && v !== null && v !== '') return v;
    }
    return null;
  };
  const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const columns = useMemo(() => {
    const base = [
      {
        title: 'Action',
        key: 'action',
        width: 80,
        fixed: 'left',
        render: (_, r) => (
          <Space size="small">
            <Tooltip title="Edit">
              <Button size="small" icon={<FaEdit />} onClick={async () => {
                setEditRow(r);
                setEditOpen(true);
                setVersionsLoading(true);
                const ver = await getExcelCombinedRowVersionsV2({ upload_id: r.upload_id, row_index: r.row_index });
                setVersions(ver.data || []);
                setVersionsLoading(false);
              }} />
            </Tooltip>
            <Popconfirm
              title="Delete this row?"
              description="This will remove the employee from this month's summary. Are you sure?"
              onConfirm={async () => {
                setLoading(true);
                try {
                  const res = await deleteCombinedRowV2({ upload_id: r.upload_id, row_index: r.row_index });
                  if (res.success) {
                    message.success(res.message);
                    fetchData();
                  } else {
                    message.error(res.message);
                  }
                } catch (e) {
                  message.error('Failed to delete row');
                } finally {
                  setLoading(false);
                }
              }}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete">
                <Button size="small" danger icon={<FaTrash />} />
              </Tooltip>
            </Popconfirm>
          </Space>
        )
      },
      {
        title: 'Empcode',
        dataIndex: 'empcode',
        width: 120,
        fixed: 'left',
        onHeaderCell: () => ({
          style: {
            position: 'sticky',
            left: 80, // Width of Action column
            zIndex: 2,
            backgroundColor: '#fafafa',
            borderRight: '1px solid #f0f0f0'
          }
        }),
        onCell: () => ({
          style: {
            position: 'sticky',
            left: 80, // Width of Action column
            zIndex: 1,
            backgroundColor: 'white',
            borderRight: '1px solid #f0f0f0'
          }
        })
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: 220,
        render: (v) => v || '-',
        fixed: 'left',
        onHeaderCell: () => ({
          style: {
            position: 'sticky',
            left: 200, // Width of Action + Empcode columns
            zIndex: 2,
            backgroundColor: '#fafafa',
            borderRight: '1px solid #f0f0f0'
          }
        }),
        onCell: () => ({
          style: {
            position: 'sticky',
            left: 200, // Width of Action + Empcode columns
            zIndex: 1,
            backgroundColor: 'white',
            borderRight: '1px solid #f0f0f0'
          }
        })
      },
    ];
    const optional = [];
    if (visibleCols.type) optional.push({ title: 'Type', dataIndex: 'employee_type', width: 120, render: t => <Tag color={t === 'company' ? 'blue' : 'purple'}>{t}</Tag> });
    if (visibleCols.company_subcontractor) optional.push({ title: 'Company/Subcontractor', dataIndex: 'company_subcontractor', width: 220 });
    if (visibleCols.client) optional.push({ title: 'Client', dataIndex: 'client', width: 180 });
    if (visibleCols.department) optional.push({ title: 'Department', dataIndex: 'department', width: 180 });
    if (visibleCols.skills) optional.push({ title: 'Skills', dataIndex: 'skills', width: 180 });
    if (visibleCols.basic_salary) optional.push({ title: 'Basic Salary', dataIndex: 'basic_salary', width: 140 });
    if (visibleCols.rate) optional.push({ title: 'Rate', dataIndex: 'rate', width: 120 });
    if (visibleCols.allowance) optional.push({
      title: 'Allowance', key: 'allowance', width: 140, render: (_, r) => {
        const a = num(getVal(r, ['allowance']));
        if (a !== null) return a;
        const sa = num(getVal(r, ['site_allowance'])) || 0;
        const oa = num(getVal(r, ['other_allowance'])) || 0;
        const total = sa + oa;
        return total ? total : '-';
      }
    });
    if (visibleCols.loan) optional.push({
      title: 'Loan', key: 'loan', width: 140, render: (_, r) => {
        const v = getVal(r, ['loan', 'loan_amount', 'loan_balance']);
        const n = num(v);
        return n !== null ? n : (v ?? '-');
      }
    });
    if (visibleCols.loan_deduction) optional.push({
      title: 'Loan Deduction', key: 'loan_deduction', width: 160, render: (_, r) => {
        const v = num(getVal(r, ['loan_deduction', 'loan_deduction_amount']));
        return v !== null ? v : '-';
      }
    });
    if (visibleCols.other_deduction) optional.push({
      title: 'Other Deduction', key: 'other_deduction', width: 160, render: (_, r) => {
        const v = num(getVal(r, ['other_deduction', 'other_deductions']));
        return v !== null ? v : '-';
      }
    });
    if (visibleCols.cleaning) optional.push({
      title: 'Cleaning', key: 'cleaning', width: 140, render: (_, r) => {
        const v = num(getVal(r, ['cleaning', 'housekeeping']));
        return v !== null ? v : '-';
      }
    });
    if (visibleCols.deductions) optional.push({
      title: 'Deductions', key: 'deductions', width: 160, render: (_, r) => {
        const v = num(getVal(r, ['deductions', 'total_deductions']));
        return v !== null ? v : '-';
      }
    });
    return [...base, ...optional, ...dayColumns, { title: 'Total', dataIndex: 'total', width: 120 }, {
      title: 'Grand Total', key: 'grand_total', width: 140, render: (_, r) => {
        const t = Number(r.total || 0);
        const rate = Number(r.rate || 0);
        if (!r.rate && !r.total) return '-';
        const v = t * rate;
        return Number.isFinite(v) ? v : '-';
      }
    }];
  }, [visibleCols, dayColumns]);

  const monthName = moment().month(filters.month - 1).format('MMMM');

  return (
    <div>
      <Card
        title={
          <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 12 }}>
            <div style={{ fontSize: '18px', fontWeight: 'bold', borderBottom: '1px solid #f0f0f0', paddingBottom: 8 }}>
              SUMMARY OF ({monthName.toUpperCase()} {filters.year})
            </div>
            {missingMatches.length > 0 && (
              <Alert
                message="Employees Missing from Ratesheet"
                description={
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
                    <span>
                      Warning: {missingMatches.length} employees were found in the timesheet but have no matching entry in the ratesheet. They will not have rates or pay calculations.
                    </span>
                    <Button
                      type="primary"
                      size="small"
                      danger
                      ghost
                      onClick={() => setShowMissingModal(true)}
                    >
                      View Missing Employees
                    </Button>
                  </div>
                }
                type="warning"
                showIcon
                closable
              />
            )}
            <Modal
              title="Missing Employees"
              open={showMissingModal}
              onCancel={() => setShowMissingModal(false)}
              footer={null}
              width={800}
            >
              <p>The following employees are present in the timesheet but could not be matched with the ratesheet:</p>
              <Table
                dataSource={missingMatches}
                columns={[
                  { title: 'Emp Code', dataIndex: 'empcode', key: 'empcode', render: t => t || <Tag color="red">Missing</Tag> },
                  { title: 'Name', dataIndex: 'name', key: 'name' },
                  { title: 'Type', dataIndex: 'employee_type', key: 'employee_type', render: t => <Tag>{t}</Tag> },
                  { title: 'Company/Subcontractor', dataIndex: 'company_subcontractor', key: 'company_subcontractor' },
                  { title: 'Client', dataIndex: 'client', key: 'client' },
                  { title: 'Department', dataIndex: 'department', key: 'department' },
                ]}
                pagination={false}
                size="small"
                rowKey={(r) => r.row_index + '_' + r.upload_id}
                scroll={{ y: 400 }}
              />
            </Modal>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Form.Item label="Month" style={{ margin: 0 }}>
                    <Select
                      style={{ width: 120 }}
                      value={filters.month}
                      options={months}
                      onChange={(v) => handleFilterChange('month', v)}
                    />
                  </Form.Item>
                  <Form.Item label="Year" style={{ margin: 0 }}>
                    <Select
                      style={{ width: 100 }}
                      value={filters.year}
                      options={years}
                      onChange={(v) => handleFilterChange('year', v)}
                    />
                  </Form.Item>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Popconfirm
                    title="Clear Monthly Summary"
                    description="This will delete all combined upload data and associated payrolls for this month. This action cannot be undone. Are you sure?"
                    onConfirm={async () => {
                      setLoading(true);
                      try {
                        const res = await clearSummaryV2({ month: filters.month, year: filters.year });
                        if (res.success) {
                          message.success(res.message);
                          fetchData();
                        } else {
                          message.error(res.message);
                        }
                      } catch (e) {
                        message.error('Failed to clear summary');
                      } finally {
                        setLoading(false);
                      }
                    }}
                    okText="Yes, Clear Everything"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                  >
                    <Button danger icon={<FaTrash />} loading={loading}>Clear Summary</Button>
                  </Popconfirm>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => {
                      uploadForm.setFieldsValue({ month: filters.month, year: filters.year, ratesheet_source: 'new' });
                      setRatesheetSource('new');
                      setRatesheetFile(null);
                      setTimesheetFile(null);
                      setUploadOpen(true);
                      setUploadError(null);
                      fetchRatesheets(filters.month, filters.year);
                    }}
                  >
                    Upload
                  </Button>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <Tag color="blue">Combined Export</Tag>
                    <Tooltip title="Download Combined Excel">
                      <Button type="primary" icon={<FaFileExcel />} loading={excelLoading} onClick={async () => {
                        try {
                          setExcelLoading(true);
                          const res = await downloadExcelCombinedV2(filters);
                          const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          const m = String(filters.month).padStart(2, '0');
                          const disp = (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || '';
                          const match = typeof disp === 'string' ? disp.match(/filename=\"?([^\";]+)\"?/) : null;
                          const filename = match ? match[1] : `combined_${filters.year}-${m}.xlsx`;
                          link.href = url;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } finally {
                          setExcelLoading(false);
                        }
                      }} />
                    </Tooltip>
                    <Tooltip title="Download Combined PDF">
                      <Button icon={<FaFilePdf />} loading={pdfLoading} onClick={async () => {
                        try {
                          setPdfLoading(true);
                          const res = await downloadExcelCombinedV2Pdf(filters);
                          const blob = new Blob([res.data], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          const m = String(filters.month).padStart(2, '0');
                          const disp = (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || '';
                          const match = typeof disp === 'string' ? disp.match(/filename=\"?([^\";]+)\"?/) : null;
                          const filename = match ? match[1] : `combined_${filters.year}-${m}.pdf`;
                          link.href = url;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } finally {
                          setPdfLoading(false);
                        }
                      }} />
                    </Tooltip>
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderLeft: '1px solid #f0f0f0', paddingLeft: 12 }}>
                    <Tag color="gold">Summary Export</Tag>
                    <Tooltip title="Download Summary Excel">
                      <Button icon={<FaFileExcel />} loading={excelLoading} onClick={async () => {
                        try {
                          setExcelLoading(true);
                          const res = await downloadExcelCombinedV2Summary(filters);
                          const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          const m = String(filters.month).padStart(2, '0');
                          const disp = (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || '';
                          const match = typeof disp === 'string' ? disp.match(/filename=\"?([^\";]+)\"?/) : null;
                          const filename = match ? match[1] : `summary_${filters.year}-${m}.xlsx`;
                          link.href = url;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } finally {
                          setExcelLoading(false);
                        }
                      }} />
                    </Tooltip>
                    <Tooltip title="Download Summary PDF">
                      <Button icon={<FaFilePdf />} loading={pdfLoading} onClick={async () => {
                        try {
                          setPdfLoading(true);
                          const res = await downloadExcelCombinedV2SummaryPdf(filters);
                          const blob = new Blob([res.data], { type: 'application/pdf' });
                          const url = window.URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          const m = String(filters.month).padStart(2, '0');
                          const disp = (res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || '';
                          const match = typeof disp === 'string' ? disp.match(/filename=\"?([^\";]+)\"?/) : null;
                          const filename = match ? match[1] : `summary_${filters.year}-${m}.pdf`;
                          link.href = url;
                          link.download = filename;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                        } finally {
                          setPdfLoading(false);
                        }
                      }} />
                    </Tooltip>
                  </div>
                  <Popover
                    trigger="click"
                    content={
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <Checkbox checked={visibleCols.type} onChange={(e) => setVisibleCols({ ...visibleCols, type: e.target.checked })}>Type</Checkbox>
                        <Checkbox checked={visibleCols.company_subcontractor} onChange={(e) => setVisibleCols({ ...visibleCols, company_subcontractor: e.target.checked })}>Company/Subcontractor</Checkbox>
                        <Checkbox checked={visibleCols.client} onChange={(e) => setVisibleCols({ ...visibleCols, client: e.target.checked })}>Client</Checkbox>
                        <Checkbox checked={visibleCols.department} onChange={(e) => setVisibleCols({ ...visibleCols, department: e.target.checked })}>Department</Checkbox>
                        <Checkbox checked={visibleCols.skills} onChange={(e) => setVisibleCols({ ...visibleCols, skills: e.target.checked })}>Skills</Checkbox>
                        <Checkbox checked={visibleCols.basic_salary} onChange={(e) => setVisibleCols({ ...visibleCols, basic_salary: e.target.checked })}>Basic Salary</Checkbox>
                        <Checkbox checked={visibleCols.rate} onChange={(e) => setVisibleCols({ ...visibleCols, rate: e.target.checked })}>Rate</Checkbox>
                        <Checkbox checked={visibleCols.allowance} onChange={(e) => setVisibleCols({ ...visibleCols, allowance: e.target.checked })}>Allowance</Checkbox>
                        <Checkbox checked={visibleCols.loan} onChange={(e) => setVisibleCols({ ...visibleCols, loan: e.target.checked })}>Loan</Checkbox>
                        <Checkbox checked={visibleCols.loan_deduction} onChange={(e) => setVisibleCols({ ...visibleCols, loan_deduction: e.target.checked })}>Loan Deduction</Checkbox>
                        <Checkbox checked={visibleCols.other_deduction} onChange={(e) => setVisibleCols({ ...visibleCols, other_deduction: e.target.checked })}>Other Deduction</Checkbox>
                        <Checkbox checked={visibleCols.cleaning} onChange={(e) => setVisibleCols({ ...visibleCols, cleaning: e.target.checked })}>Cleaning</Checkbox>
                        <Checkbox checked={visibleCols.deductions} onChange={(e) => setVisibleCols({ ...visibleCols, deductions: e.target.checked })}>Deductions</Checkbox>
                      </div>
                    }
                  >
                    <Tooltip title="Columns">
                      <Button icon={<FaColumns />} />
                    </Tooltip>
                  </Popover>
                </div>
              </div>
            </div>
          </div>
        }
      >
        <Form layout="inline" style={{ width: '100%', marginTop: 8, marginBottom: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, width: '100%' }}>
            <div style={{ display: 'flex', gap: 8, flex: 1 }}>
              <Form.Item label="Company" style={{ margin: '0 0 8px 0' }}>
                <Select
                  allowClear
                  style={{ width: 180 }}
                  value={filters.company}
                  options={departmentOptions.map(dept => ({
                    label: `${dept.label}`,
                    value: dept.value,
                    shortname: dept.shortname
                  }))}
                  onChange={(v) => handleFilterChange('company', v)}
                  showSearch
                  optionFilterProp="shortname"
                  filterOption={(input, option) => {
                    if (!input) return true;
                    const inputLower = input.toLowerCase();
                    return (
                      (option.label && option.label.toLowerCase().includes(inputLower)) ||
                      (option.shortname && option.shortname.toLowerCase().includes(inputLower))
                    );
                  }}
                  placeholder="Search by name or code..."
                />
              </Form.Item>
              <Form.Item label="Client" style={{ margin: '0 0 8px 0' }}>
                <Select
                  allowClear
                  style={{ width: 180 }}
                  value={filters.client}
                  options={clientOptions}
                  onChange={(v) => handleFilterChange('client', v)}
                  placeholder="Select client"
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option.label || '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item label="Department" style={{ margin: '0 0 8px 0' }}>
                <Select
                  allowClear
                  style={{ width: 180 }}
                  value={filters.department}
                  options={filters.client ? clientDepartments[filters.client] || [] : []}
                  onChange={(v) => handleFilterChange('department', v)}
                  disabled={!filters.client || !clientDepartments[filters.client]?.length}
                  placeholder={
                    !filters.client
                      ? "Select client first"
                      : (clientDepartments[filters.client]?.length
                        ? "Select department"
                        : "No departments available")
                  }
                  showSearch
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    (option.label || '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
              <Form.Item label="Subcontractor" style={{ margin: '0 0 8px 0' }}>
                <Select
                  allowClear
                  style={{ width: 160 }}
                  value={filters.subcontractor}
                  options={subcontractorOptions}
                  onChange={(v) => handleFilterChange('subcontractor', v)}
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select Subcontractor"
                  filterOption={(input, option) =>
                    (option.label || '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Form.Item style={{ margin: 0 }}>
                {/* Removed Filter button */}
              </Form.Item>
            </div>
          </div>
        </Form>
        <Modal
          open={editOpen}
          title="Edit Row"
          onCancel={() => { setEditOpen(false); setEditRow(null); }}
          onOk={async () => {
            if (!editRow) return;
            try {
              setEditSaving(true);
              const days = {};
              for (let d = 1; d <= daysInMonth; d++) {
                const key = `day_${d}`;
                days[key] = editRow[key] ?? null;
              }
              await updateExcelCombinedRowV2({ upload_id: editRow.upload_id, row_index: editRow.row_index, empcode: editRow.empcode, name: editRow.name, rate: editRow.rate ?? null, days });
              setEditOpen(false);
              setEditRow(null);
              fetchData();
            } finally {
              setEditSaving(false);
            }
          }}
          confirmLoading={editSaving}
          width={800}
        >
          {editRow && (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <div>
                  Rate
                  <InputNumber style={{ width: '100%' }} min={0} step={0.5} value={editRow.rate ?? null} onChange={(v) => setEditRow({ ...editRow, rate: v })} />
                </div>
                <div>
                  Total
                  <InputNumber style={{ width: '100%' }} min={0} step={0.5} value={editRow.total ?? null} disabled />
                </div>
              </div>
              <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const d = i + 1; const key = `day_${d}`;
                  return (
                    <div key={key}>
                      {`Day ${d}`}
                      <InputNumber style={{ width: '100%' }} min={0} step={0.5} value={editRow[key] ?? null} onChange={(v) => {
                        const next = { ...editRow, [key]: v };
                        let sum = 0;
                        for (let j = 1; j <= daysInMonth; j++) { const k = `day_${j}`; sum += Number(next[k] || 0); }
                        next.total = sum;
                        setEditRow(next);
                      }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 16 }}>
                <strong>Versions</strong>
                {versionsLoading ? <div>Loading...</div> : (
                  <div style={{ maxHeight: 180, overflow: 'auto', border: '1px solid #eee', padding: 8 }}>
                    {(versions || []).map((v, idx) => (
                      <div key={idx} style={{ marginBottom: 8 }}>
                        <div>{new Date(v.created_at).toLocaleString()} — {v.section}</div>
                        <div>Old: {JSON.stringify(v.old_values)}</div>
                        <div>New: {JSON.stringify(v.new_values)}</div>
                      </div>
                    ))}
                    {(versions || []).length === 0 && <div>No versions</div>}
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal>
        <style>{`.compact-table .ant-table-cell, .compact-table .ant-table-thead > tr > th { padding: 4px 6px; font-size: 12px; }`}</style>
        <div style={{ overflow: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
          <Table
            rowKey={(r) => `${r.upload_id}-${r.empcode}-${r.row_index}`}
            loading={loading}
            dataSource={filteredRows}
            columns={columns}
            size="small"
            scroll={{ x: 'max-content' }}
            className="compact-table"
            pagination={{ pageSize: 200 }}
            rowClassName={(r) => (!r.match_ok ? 'missing-record-row' : '')}
            onRow={(r) => ({ style: !r.match_ok ? { cursor: 'pointer' } : {} })}
            sticky
          />
        </div>
        <style>{`
          .missing-record-row { background-color: #fff1f0 !important; }
          .missing-record-row:hover > td { background-color: #ffa39e !important; }
        `}</style>
      </Card>

      <Modal
        open={uploadOpen}
        title="Upload Timesheet / Ratesheet"
        onCancel={() => setUploadOpen(false)}
        onOk={onUploadSubmit}
        confirmLoading={uploadLoading}
        width={500}
      >
        {uploadError && (
          <Alert
            message={uploadError.message || "Error"}
            description={
              uploadError.data?.missing_in_ratesheet ? (
                <div>
                  <p>The following employee codes from the timesheet were not found in the ratesheet:</p>
                  <ul style={{ maxHeight: '150px', overflowY: 'auto', paddingLeft: '15px', marginBottom: 0 }}>
                    {uploadError.data.missing_in_ratesheet.map(code => (
                      <li key={code}><strong>{code}</strong></li>
                    ))}
                  </ul>
                </div>
              ) : null
            }
            type="error"
            showIcon
            closable
            onClose={() => setUploadError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        <Form form={uploadForm} layout="vertical" initialValues={{ month: filters.month, year: filters.year, ratesheet_source: 'new' }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <Form.Item name="month" label="Month" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select options={months} onChange={(v) => fetchRatesheets(v, uploadForm.getFieldValue('year'))} />
            </Form.Item>
            <Form.Item name="year" label="Year" rules={[{ required: true }]} style={{ flex: 1 }}>
              <Select options={years} onChange={(v) => fetchRatesheets(uploadForm.getFieldValue('month'), v)} />
            </Form.Item>
          </div>
          <Form.Item label="Ratesheet Source">
            <Radio.Group value={ratesheetSource} onChange={(e) => {
              setRatesheetSource(e.target.value);
              fetchRatesheets(uploadForm.getFieldValue('month'), uploadForm.getFieldValue('year'));
            }}>
              <Radio value="new">Upload New</Radio>
              <Radio value="previous">Use Previous</Radio>
            </Radio.Group>
          </Form.Item>
          {ratesheetSource === 'previous' ? (
            <Form.Item name="previous_ratesheet_id" label="Previous Ratesheet" rules={[{ required: true }]}>
              <Select options={ratesheetOptions} showSearch optionFilterProp="label" placeholder="Select a ratesheet from this month" />
            </Form.Item>
          ) : (
            <Form.Item label="Ratesheet (Optional if only updating timesheet)">
              <Upload
                beforeUpload={file => { setRatesheetFile(file); return false; }}
                showUploadList={ratesheetFile ? [{ name: ratesheetFile.name }] : false}
                maxCount={1}
                accept=".xlsx,.xls,.csv"
              >
                <Button icon={<UploadOutlined />}>Choose Ratesheet</Button>
              </Upload>
            </Form.Item>
          )}
          <Form.Item label="Timesheet (Optional if only updating ratesheet)">
            <Upload
              beforeUpload={file => { setTimesheetFile(file); return false; }}
              showUploadList={timesheetFile ? [{ name: timesheetFile.name }] : false}
              maxCount={1}
              accept=".xlsx,.xls,.csv"
            >
              <Button icon={<UploadOutlined />}>Choose Timesheet</Button>
            </Upload>
          </Form.Item>
          <div style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>
            Note: Employees with matching <strong>Employee Codes</strong> or <strong>Names</strong> will be updated; others will be added as new entries.
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ExcelCombinedView;
